import { useMemo } from 'react';
import { sortBy } from 'lodash';
import { DAMNodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { TreeMode } from '@/components/sidebar/tree/tree';

export type FlattenedTreeItem = {
  treeBranch: TreeRecursiveChildrenNode;
  depth: number;
  parentId?: string;
};

/**
 * Optimized hook for flattening tree structure with caching
 * This hook memoizes the flattening operation and uses cached sorting
 * to avoid expensive re-computations when openIds change.
 */
export const useFlattenedTree = (
  tree: Array<TreeRecursiveChildrenNode>,
  openIds: Record<string, boolean>,
  mode?: TreeMode,
  shouldSort = true,
) => {
  const sortedTreeCache = useMemo(() => {
    if (!shouldSort) {
      return tree;
    }

    const cache = new Map<string, Array<TreeRecursiveChildrenNode>>();

    const sortTree = (items: Array<TreeRecursiveChildrenNode>): Array<TreeRecursiveChildrenNode> => {
      const cacheKey = items.map((item) => item.id).join(',');

      if (cache.has(cacheKey)) {
        return cache.get(cacheKey)!;
      }

      const sorted = sortBy(items, (item) => item.node.name.toLowerCase());
      cache.set(cacheKey, sorted);
      return sorted;
    };

    return sortTree(tree);
  }, [tree, shouldSort]);

  const flattenedTree = useMemo(() => {
    const result: Array<FlattenedTreeItem> = [];

    const flatten = (items: Array<TreeRecursiveChildrenNode>, parentId?: string, depth = 0): void => {
      const itemsToProcess = shouldSort ? sortBy(items, (item) => item.node.name.toLowerCase()) : items;

      for (const treeBranch of itemsToProcess) {
        // Skip albums in SelectFolder mode, this logic could probably be improved.
        if (mode === TreeMode.SelectFolder && treeBranch.type === DAMNodeType.Album) {
          continue;
        }

        result.push({ treeBranch, depth, parentId });

        if (openIds[treeBranch.id] && treeBranch.children && treeBranch.children.length > 0) {
          flatten(treeBranch.children, treeBranch.id, depth + 1);
        }
      }
    };

    flatten(sortedTreeCache, undefined, 0);
    return result;
  }, [sortedTreeCache, openIds, mode, shouldSort]);

  return flattenedTree;
};

/**
 * Hook for flattening search results
 * Search results are always shown at depth 0 without hierarchy.
 */
export const useFlattenedSearchResults = (
  searchResults?: Array<TreeRecursiveChildrenNode>,
): Array<FlattenedTreeItem> => {
  return useMemo(() => {
    if (!searchResults?.length) {
      return [];
    }

    return searchResults.map((treeBranch) => ({
      treeBranch: {
        ...treeBranch,
        children: [],
        with_children: false,
      },
      depth: 0,
      parentId: undefined,
    }));
  }, [searchResults]);
};
